import * as React from "react";
import { CalendarIcon, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { PHONE_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

/** An inclusive, day-granularity range in local time. */
export type DateRangeValue = { from: Date; to: Date };

// Small local-time date helpers. Kept here rather than imported from
// date-fns: its v4 type definitions don't resolve under this project's
// TypeScript 4.9 / moduleResolution "node" build (CRA's type checker).
const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth() + n, 1);

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

/** "29 Sep 2026" */
export function formatDay(d: Date): string {
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** From the 1st of the month `monthsBack - 1` months ago through today -
 *  e.g. 6 → the current month plus the five before it. */
export function trailingMonthsRange(monthsBack: number): DateRangeValue {
  const today = new Date();
  return {
    from: startOfMonth(addMonths(today, -(monthsBack - 1))),
    to: startOfDay(today)
  };
}

/** True when an ISO timestamp falls on or between the range's two days
 *  (whole days, local time - `to` includes everything up to 23:59:59). */
export function isWithinRange(iso: string, range: DateRangeValue): boolean {
  const t = new Date(iso).getTime();
  return (
    t >= startOfDay(range.from).getTime() && t <= endOfDay(range.to).getTime()
  );
}

type Draft = { from?: Date; to?: Date };

/** Day-level From → To range picker, built on the shadcn Calendar in range
 *  mode (two months side by side from sm up, one on phones), with an
 *  Apply / Cancel footer. The first click sets From, the second sets To (a
 *  day before From swaps the two); a third click starts a new range.
 *  Nothing is applied - so no data refilters - until Apply is clicked;
 *  Cancel or closing the popover discards the selection. Apply accepts a
 *  single clicked day as a one-day range.
 *
 *  `allowClear` adds a clear (×) button beside the trigger and an "All
 *  dates" empty state (value null) for optional filters, e.g. Audit Logs;
 *  without it a range is always set (Dashboard). Days after `maxDate`
 *  (default: today) are not selectable. */
export function DateRangePicker({
  value,
  onChange,
  allowClear = false,
  placeholder = "All dates",
  maxDate,
  align = "start",
  disabled,
  className
}: {
  value: DateRangeValue | null;
  onChange: (value: DateRangeValue | null) => void;
  allowClear?: boolean;
  placeholder?: string;
  maxDate?: Date;
  align?: "start" | "center" | "end";
  disabled?: boolean;
  className?: string;
}) {
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [open, setOpen] = React.useState(false);
  // null = not picking yet, so the calendar shows the applied range.
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [month, setMonth] = React.useState<Date>(() => new Date());

  const lastDay = startOfDay(maxDate ?? new Date());

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(null);
      const anchor = value?.to ?? lastDay;
      setMonth(startOfMonth(isPhone ? anchor : addMonths(anchor, -1)));
    }
    setOpen(next);
  };

  const pickDay = (day: Date) => {
    const d = startOfDay(day);
    if (!draft?.from || draft.to) {
      // No selection yet, or a complete one - start a new range here.
      setDraft({ from: d });
      return;
    }
    setDraft(
      d < draft.from ? { from: d, to: draft.from } : { from: draft.from, to: d }
    );
  };

  const pending = draft?.from
    ? { from: draft.from, to: draft.to ?? draft.from }
    : null;
  const apply = () => {
    if (!pending) return;
    onChange(pending);
    setOpen(false);
  };

  const shown = draft?.from
    ? { from: draft.from, to: draft.to }
    : value
      ? { from: value.from, to: value.to }
      : undefined;
  const showClear = allowClear && !!value && !disabled;

  return (
    <div className={cn("relative inline-flex", className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              "h-9 w-full justify-start font-normal",
              !value && "text-muted-foreground",
              showClear && "pr-9"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
            <span className="truncate">
              {value
                ? `${formatDay(value.from)} – ${formatDay(value.to)}`
                : placeholder}
            </span>
          </Button>
        </PopoverTrigger>
        {/* Capped to the space Radix reports as free on screen (it also flips
            above the trigger when there's more room there); the calendar
            scrolls inside that cap if needed, while the Apply/Cancel footer
            stays pinned and always visible. */}
        <PopoverContent
          align={align}
          collisionPadding={12}
          className="flex max-h-[var(--radix-popover-content-available-height)] w-auto max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden p-0"
        >
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <Calendar
              mode="range"
              selected={shown}
              onSelect={(_, triggerDate) => pickDay(triggerDate)}
              month={month}
              onMonthChange={setMonth}
              numberOfMonths={isPhone ? 1 : 2}
              endMonth={lastDay}
              disabled={{ after: lastDay }}
            />
          </div>
          <div className="flex shrink-0 items-center justify-between gap-3 border-t bg-popover px-3 py-2.5">
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {!draft?.from
                ? "Select a start date"
                : !draft.to
                  ? `${formatDay(draft.from)} – select an end date`
                  : `${formatDay(draft.from)} – ${formatDay(draft.to)}`}
            </span>
            <div className="flex shrink-0 gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-8"
                disabled={!pending}
                onClick={apply}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {showClear && (
        <button
          type="button"
          aria-label="Clear date filter"
          onClick={() => onChange(null)}
          className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
